import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { Project } from '../../types';
import ToolLogo from '../ui/ToolLogo';
import { LogoSlug } from '../../data/logos';

interface CaseStudy {
  id: string;
  title: string;
  kind: string;
  tagline: string;
  status: string;
  accent: string;
  /** What the product is and who it is for. */
  summary: string;
  /** What I personally did on it. */
  role: string;
  built: string[];
  logos: LogoSlug[];
  /** Stack items and capabilities with no brand mark. */
  extras: string[];
  link?: { href: string; label: string };
}

// Flagship products — each one designed, built and run end-to-end.
const caseStudies: CaseStudy[] = [
  {
    id: 'onoffdeals',
    title: 'OnOffDeals',
    kind: 'saas',
    tagline: 'AI-powered real-estate deal sourcing & analysis SaaS',
    status: 'live',
    accent: '#22e6ff',
    summary:
      'A subscription SaaS that helps real-estate investors find deals, pull comps and rent data, and run the numbers before they make an offer.',
    role: 'Sole Developer & Architect',
    built: [
      'Built the valuation engine from scratch: ARV, rent estimates, comps, MAO, cap rate, cash-on-cash and DSCR',
      'React 18 SPA on an async FastAPI + MongoDB backend, with 16 API routers and 20 domain services',
      '311 automated tests guarding the valuation math and billing logic',
      'Live Stripe billing: subscriptions, trials, coupons, webhook-driven state and per-plan usage metering',
      'Claude as an in-app deal coach, with prompt guardrails and output validation',
    ],
    logos: ['react', 'tailwindcss', 'fastapi', 'python', 'mongodb', 'redis', 'stripe', 'anthropic'],
    extras: ['JWT auth', 'Vite'],
    link: { href: 'https://onoffdeals.ai', label: 'onoffdeals.ai' },
  },
  {
    id: 'gurukin',
    title: 'Gurukin',
    kind: 'edtech',
    tagline: 'AI-assisted learning & exam-prep platform',
    status: 'live · expanding',
    accent: '#2bffb0',
    summary:
      'A free, ad-free learning platform with openly licensed content. Every track is AI-drafted, then human-reviewed and fact-checked against the source documentation.',
    role: 'Creator & Sole Developer',
    built: [
      'Claude Certified Developer – Foundations prep: 758 practice questions, a fresh 53-item mock every sitting, and six chapters (~33 hours) weighted to the exam blueprint',
      'Japanese (JLPT N5) track: a 100-day roadmap covering 105 kanji, 819 words and 131 grammar points',
      'No-account design: progress saves in the browser, so there is no sign-up wall',
      'Kids’ track in development: daily missions, adaptive difficulty and a parent dashboard, aligned to CBSE/NCERT and Common Core',
      'Child-safe by design: no open chatbot, and every lesson comes from approved, human-reviewed curriculum',
    ],
    logos: [],
    extras: [
      'LLM content pipeline',
      'Human-in-the-loop review',
      'Exam-blueprint weighting',
      'Local-first progress',
    ],
    link: { href: 'https://gurukin.app', label: 'gurukin.app' },
  },
  {
    id: 'ops-portal',
    title: 'PowerSmith Operations Portal',
    kind: 'internal platform',
    tagline: 'Production sales & operations platform for a rep-driven solar team',
    status: 'in production',
    accent: '#ffb13d',
    summary:
      'An operations layer for what a CRM handles badly: lead attribution, referral partners, commissions, org structure and rep onboarding. It mirrors data into GoHighLevel instead of replacing it.',
    role: 'AI Full-Stack Developer',
    built: [
      '15 screens behind three role tiers: Rep (8 screens), Team, and Admin (all 15)',
      'Referral and affiliate tracking where every lead carries both the partner and the rep, with per-partner links and live dashboards',
      'Commission engine that derives payouts from pipeline stage on configurable bands, with per-role adjustments and no manual reconciliation',
      'Live org chart with recruitment lines, plus training, SOPs and brand assets for every rep',
      'A digital business card with QR code and a referral link issued with every account',
    ],
    logos: ['react', 'tailwindcss', 'fastapi', 'python', 'mongodb', 'anthropic', 'gohighlevel'],
    extras: ['JWT auth', 'Cloudflare R2', 'PDF statements', 'QR generation'],
    link: {
      href: 'https://charyxlabs.tech/ops-portal',
      label: 'productized at charyxlabs.tech',
    },
  },
];

const ProjectsSection: React.FC = () => {
  const { themeConfig } = useTheme();

  const projectsData: Project[] = [
    {
      id: 'CareerAgent-AI-Powered-Job-Assistant',
      title: 'CareerAgent — AI Job Assistant',
      description:
        'AI-powered agent for career guidance, resume parsing and job-search automation.',
      technologies: ['Python', 'AI Agent', 'NLP'],
      githubUrl: 'https://github.com/sharmachaitanya945/CareerAgent-AI-Powered-Job-Assistant',
      featured: true,
      imageUrl: '/assets/careerAgentAi.png',
    },
    {
      id: 'legalease',
      title: 'LegalEase AI',
      description:
        'Legal research app powered by MindsDB, Ollama and Gemini — semantic search and AI legal assistance.',
      technologies: ['Python', 'Flask', 'MindsDB', 'Gemini', 'Docker'],
      githubUrl: 'https://github.com/sharmachaitanya945/legalease',
      featured: true,
      imageUrl: '/assets/legalease.png',
    },
    {
      id: 'PDF2CSV-AI',
      title: 'PDF2CSV-AI',
      description:
        'Automation tool that converts PDF bank statements to clean CSV using deep-learning table extraction.',
      technologies: ['Python', 'AI', 'Automation', 'PDF'],
      githubUrl: 'https://github.com/sharmachaitanya945/PDF2CSV-AI',
      featured: true,
      imageUrl: '/assets/pdf2Csv.png',
    },
    {
      id: 'practo-doctor-scraper',
      title: 'Practo Doctor Scraper',
      description:
        'Web-scraping automation that extracts doctor and clinic data from Practo at scale.',
      technologies: ['Python', 'Scraping', 'Data Extraction'],
      githubUrl: 'https://github.com/sharmachaitanya945/practo-doctor-scraper',
      imageUrl: '/assets/practoDoctorScraper.png',
    },
    {
      id: 'face-recognition-system',
      title: 'Face Recognition System',
      description:
        'Real-time face detection and recognition pipeline built with OpenCV and deep learning.',
      technologies: ['Python', 'OpenCV', 'Computer Vision'],
      githubUrl: 'https://github.com/sharmachaitanya945/face-recognition-system',
      imageUrl: '/assets/faceRecognitionSystem.png',
    },
    {
      id: '3d_model_generator',
      title: '3D Model Generator',
      description:
        'Generates 3D models from data using Python, AI and computer-vision techniques.',
      technologies: ['Python', '3D', 'AI'],
      githubUrl: 'https://github.com/sharmachaitanya945/3d_model_generator',
      imageUrl: '/assets/threeDModelGenerator.png',
    },
  ];

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  return (
    <section id="projects" className={themeConfig.layout?.sectionStyle}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
        className="mb-12 max-w-2xl"
      >
        <div className="mb-3 flex items-center gap-2">
          <span className="h-px w-6 bg-neon-green/60" />
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-neon-green">
            {'// featured projects'}
          </span>
        </div>
        <h2 className="font-display text-3xl font-bold leading-tight text-ink-main sm:text-4xl">
          Products I&apos;ve <span className="gradient-text">built &amp; shipped</span>
        </h2>
        <p className="mt-3 text-lg text-ink-soft">
          Production software, not demos. Each one shows what it is, what I
          personally built, and what it runs on.
        </p>
      </motion.div>

      <div className="space-y-8">
        {caseStudies.map((cs, i) => (
          <CaseStudyCard key={cs.id} cs={cs} index={i} />
        ))}
      </div>

      <h3 className="mb-6 mt-16 font-mono text-sm uppercase tracking-widest text-neon-purple">
        earlier builds &amp; experiments
      </h3>
      <motion.div
        className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-100px' }}
      >
        {projectsData.map((project) => (
          <ProjectCard key={project.id} project={project} />
        ))}
      </motion.div>
    </section>
  );
};

const CaseStudyCard: React.FC<{ cs: CaseStudy; index: number }> = ({ cs, index }) => (
  <motion.article
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.55 }}
    className="n8n-node overflow-hidden p-0"
    style={{ borderColor: `${cs.accent}40` }}
  >
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.35fr]">
      {/* overview */}
      <div
        className="flex flex-col border-b p-6 lg:border-b-0 lg:border-r"
        style={{
          borderColor: `${cs.accent}33`,
          background: `linear-gradient(135deg, ${cs.accent}14, transparent 70%)`,
        }}
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <span
            className="font-mono text-[10px] uppercase tracking-widest"
            style={{ color: cs.accent }}
          >
            {String(index + 1).padStart(2, '0')} · {cs.kind}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-neon-green/30 bg-canvas-base/60 px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-neon-green">
            <span className="live-dot" /> {cs.status}
          </span>
        </div>

        <h3 className="font-display text-2xl font-bold text-ink-main">{cs.title}</h3>
        <p className="mt-1 font-mono text-xs" style={{ color: cs.accent }}>
          {cs.tagline}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-ink-soft">{cs.summary}</p>

        <div className="mt-5 rounded-lg border border-canvas-border/60 bg-canvas-deep/60 px-3 py-2.5">
          <p className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            my role
          </p>
          <p className="mt-0.5 font-display text-sm font-semibold text-ink-main">
            {cs.role}
          </p>
        </div>

        {cs.link && (
          <a
            href={cs.link.href}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-1.5 self-start font-mono text-xs text-neon-cyan hover:underline lg:mt-auto lg:pt-5"
          >
            {cs.link.label}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M7 17 17 7M9 7h8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        )}
      </div>

      {/* what I built + stack */}
      <div className="p-6">
        <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          what i built
        </p>
        <ul className="space-y-2">
          {cs.built.map((b) => (
            <li key={b} className="flex gap-2 text-sm leading-relaxed text-ink-soft">
              <span style={{ color: cs.accent }}>▹</span>
              {b}
            </li>
          ))}
        </ul>

        <p className="mb-3 mt-6 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          stack
        </p>
        <div className="flex flex-wrap gap-1.5">
          {cs.logos.map((slug) => (
            <ToolLogo key={slug} slug={slug} size={16} />
          ))}
          {cs.extras.map((x) => (
            <span key={x} className="tech-chip !py-1 !text-[11px]">
              {x}
            </span>
          ))}
        </div>
      </div>
    </div>
  </motion.article>
);

const ProjectCard: React.FC<{ project: Project }> = ({ project }) => {
  const itemAnimation = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <motion.div variants={itemAnimation} className="n8n-node group overflow-hidden p-0">
      <div className="relative h-44 overflow-hidden">
        <img
          src={project.imageUrl || 'https://via.placeholder.com/500x300?text=No+Image'}
          alt={project.title}
          loading="lazy"
          className="h-full w-full object-cover opacity-90 transition-transform duration-500 group-hover:scale-105"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-canvas-node via-canvas-node/20 to-transparent" />
        {project.featured && (
          <div className="absolute right-3 top-3 rounded-full border border-neon-cyan/50 bg-canvas-base/80 px-2 py-1 font-mono text-[10px] uppercase tracking-wide text-neon-cyan backdrop-blur">
            featured
          </div>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-display text-lg font-semibold text-ink-main">
          {project.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-ink-soft">
          {project.description}
        </p>

        <div className="mt-4 flex flex-wrap gap-1.5">
          {project.technologies.map((tech, index) => (
            <span key={index} className="tech-chip !py-1 !text-[11px]">
              {tech}
            </span>
          ))}
        </div>

        <div className="mt-5 flex items-center gap-4">
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-xs text-neon-cyan hover:underline"
            >
              live demo →
            </a>
          )}
          {project.githubUrl && project.id !== 'Portfolio Website' && (
            <a
              href={project.githubUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 font-mono text-xs text-ink-soft transition-colors hover:text-neon-cyan"
            >
              <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fillRule="evenodd"
                  d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"
                  clipRule="evenodd"
                />
              </svg>
              code
            </a>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProjectsSection;
